import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { euroInt, stripParen, priceTtcOf, setDisplayCurrency } from "@/lib/format";
import { Loader2, ChevronRight, ArrowLeft, Package } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { isPromoOn } from "@/lib/promoChoice";

export default function ModelVersions() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const range = params.get("range");
  const group = params.get("group");
  const family = params.get("family");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [promos, setPromos] = useState([]);
  const countryCode = localStorage.getItem("fautras_country") || "FR";
  const nav = useNavigate();

  useEffect(() => {
    if (!range) { setLoading(false); return; }
    api.get(`/menu/${range}`).then(({ data }) => setGroups(data.groups || [])).finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!range || !countryCode) { setPromos([]); return; }
    api.get(`/promotions/active`, { params: { country: countryCode, range_key: range } })
      .then(({ data }) => setPromos(data || [])).catch(() => setPromos([]));
  }, [range, countryCode]);

  const vat = useMemo(() => {
    const c = countries.find((x) => x.code === countryCode);
    return c ? c.vat_rate : 0.2;
  }, [countries, countryCode]);
  const modelPubMap = useMemo(() => {
    const m = {};
    for (const p of promos.filter(isPromoOn)) for (const [id, r] of Object.entries(p.model_rules || {})) if ((r.pub || 0) > (m[id] || 0)) m[id] = r.pub || 0;
    return m;
  }, [promos]);
  const [loc, setLoc] = useState({ models: {}, pricing: null });
  useEffect(() => {
    if (range !== "van") { setLoc({ models: {}, pricing: null }); return; }
    api.get(`/catalog/${range}`, { params: { country: countryCode } })
      .then(({ data }) => setLoc({ models: Object.fromEntries((data.models || []).map((m) => [m.id, m])), pricing: data.pricing || null }))
      .catch(() => setLoc({ models: {}, pricing: null }));
  }, [range, countryCode]);
  setDisplayCurrency(loc.pricing?.currency, loc.pricing?.rate);
  const priceTtc = (model) => priceTtcOf(loc.models[model.id] || model, vat) * (1 - (modelPubMap[model.id] || 0) / 100);

  const groupDef = useMemo(() => groups.find((g) => g.key === group), [groups, group]);
  const entry = useMemo(() => (groupDef?.entries || []).find((e) => e.type === "family" && e.label === family), [groupDef, family]);
  const versions = entry?.versions || [];
  const familyImg = useMemo(() => {
    const withImg = versions.filter((v) => v.model?.image);
    const classic = withImg.find((v) => /classic/i.test(v.label) || /classic/i.test(v.model?.name || ""));
    return (classic || withImg[0] || versions[0])?.model?.image || "";
  }, [versions]);

  const go = (m) => nav(`/configurer?range=${range}&group=${group}&model=${m.id}`);
  const backUrl = `/modeles?range=${range}&group=${group}`;

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Link to={backUrl} data-testid="versions-back" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour aux modèles
      </Link>

      <div className="flex items-center gap-4 mb-5">
        {familyImg && (
          <div className="hidden sm:flex w-28 h-20 rounded-xl bg-white border border-border items-center justify-center overflow-hidden shrink-0">
            <img src={familyImg} alt={family} className="max-h-full max-w-full object-contain p-1.5" />
          </div>
        )}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{t("Choisissez votre version")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 uppercase">{t(range === "van" ? stripParen(family) : family)}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{versions.length} {t("versions disponibles.")}</p>
        </div>
      </div>

      {versions.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-xl text-muted-foreground">
          {t("Aucune version disponible.")}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="versions-grid">
          {versions.map((v, i) => {
            const img = v.model?.image || familyImg;
            return (
              <motion.button
                key={v.label}
                data-testid={`version-${v.label}`}
                onClick={() => go(v.model)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="text-left rounded-2xl border border-border bg-card overflow-hidden transition-all duration-300 hover:border-primary/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 group"
              >
                <div className="aspect-[4/3] bg-white flex items-center justify-center overflow-hidden">
                  {img
                    ? <img src={img} alt={v.label} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]" />
                    : <Package className="w-12 h-12 text-slate-300" strokeWidth={1.25} />}
                </div>
                <div className="px-5 py-4 flex items-center justify-between gap-2 border-t border-border/60">
                  <div className="min-w-0">
                    <div className="font-display text-base font-bold leading-snug line-clamp-2 min-h-[2.75em] uppercase">{t(range === "van" ? stripParen(v.label) : v.label)}</div>
                    <div className="text-base font-mono text-[#9E7B41] font-semibold mt-1">
                      {euroInt(priceTtc(v.model))} <span className="text-xs text-muted-foreground font-sans">{t("TTC")}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
